/**
 * Barebones EventEmitter implementation for API namespaces.
 */
import { nanoid } from "nanoid";
import { checkIfCompatibleWithBackend } from "./checkIfCompatibleWithBackend.js";
const safeWindowToDesktop = typeof window !== "undefined" && window["todesktop"];
// TODO: Remove legacy event support in 2.0
/**
 * Setup the ability to register an event handler for the given `eventMappings`.
 * @internal
 */
export const setupAddEventListener = (eventMappings) => async (event, callback) => {
    const eventId = nanoid();
    const eventsToListenFor = event === "*" ? Object.keys(eventMappings) : [event];
    for (const eventName of eventsToListenFor) {
        const legacyName = eventMappings[eventName];
        safeWindowToDesktop === null || safeWindowToDesktop === void 0 ? void 0 : safeWindowToDesktop.on(legacyName, callback, eventId);
    }
    const off = setupRemoveEventListener(eventMappings);
    return async () => off(event, callback, eventId);
};
export const typedSetupAddEventListener = (eventMappings) => async (event, callback) => {
    const eventId = nanoid();
    const eventsToListenFor = event === "*" ? Object.keys(eventMappings) : [event];
    for (const eventName of eventsToListenFor) {
        const legacyName = eventMappings[eventName];
        safeWindowToDesktop === null || safeWindowToDesktop === void 0 ? void 0 : safeWindowToDesktop.on(legacyName, callback, eventId);
    }
    const off = setupRemoveEventListener(eventMappings);
    return async () => off(event, callback, eventId);
};
// TODO: Remove legacy event support in 2.0
/**
 * Setup the ability to unregister an event handler for the given `eventMappings`.
 * @internal
 */
export const setupRemoveEventListener = (eventMappings) => (event, callback, eventId) => {
    const eventsToListenFor = event === "*" ? Object.keys(eventMappings) : [event];
    for (const eventName of eventsToListenFor) {
        const legacyName = eventMappings[eventName];
        safeWindowToDesktop === null || safeWindowToDesktop === void 0 ? void 0 : safeWindowToDesktop.off(legacyName, callback, eventId);
    }
};
/**
 * Method to interact with ToDesktop's messaging API.
 *
 * @privateRemarks
 * Use this API to invoke exposed methods from wthin the renderer.
 * It provides additional type safety, and a standard interface for mapping
 * calls to the global `todesktop.invoke()` function.
 *
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createInvokeWithMetadata(name, version) {
    const metadata = {
        package: { name, version },
        invoke: { version: "1.0.0" },
    };
    function invoke(command) {
        const options = {
            metadata,
            payload: {
                command,
            },
        };
        return window["todesktop"]["invoke"](options);
    }
    function construct(command) {
        const options = {
            metadata,
            payload: {
                command: Object.assign(Object.assign({}, command), { method: ":new" }),
            },
        };
        return window["todesktop"]["invoke"](options);
    }
    function releaseMemory(memoryRefs) {
        // TODO: find out desktopify version below
        if (checkIfCompatibleWithBackend("releaseMemory", "4.6.0")) {
            if (!(memoryRefs === null || memoryRefs === void 0 ? void 0 : memoryRefs.length)) {
                // eslint-disable-next-line no-console
                console.log("No memory references to release");
                return;
            }
            return window["todesktop"]["releaseMemory"](memoryRefs);
        }
    }
    return { invoke, construct, releaseMemory };
}
export { checkIfCompatibleWithPlugin } from "./checkIfCompatibleWithPlugin.js";
export { checkIfCompatibleWithBackend, isCompatibleWithBackend, } from "./checkIfCompatibleWithBackend.js";
